<template>
  <div ref="containerRef" class="scrollbar" @scroll="onScroll">
    <slot />

    <div v-if="loading"
         class="loader">
      <Spinner />
    </div>
  </div>
</template>
<script setup>
import Spinner from "@/js/Elements/Spinner.vue";
import {useStore} from "vuex";
import {onBeforeUnmount, onMounted, ref} from "vue";

const store = useStore();
const props = defineProps({
  options: Object,
  loading: Boolean,
});
const emit = defineEmits(['scroll', 'scrollEnd', 'scrollStart', 'scrollStartX', 'scrollEndX']);
const containerRef = ref();
const OFFSET = 10;
let timer = null;
let observer = null;

defineExpose({
  getInstance() {
    return containerRef.value;
  },
  scrollToBottom() {
    setTimeout(() => {
      if (!containerRef.value) return;
      containerRef.value.scrollTop = containerRef.value.scrollHeight;
    }, 100);
  },

  scrollTo(position) {
    if (!containerRef.value.value) return;
    containerRef.value.scrollTop = position;
  },

  isScrollable() {
    return containerRef.value.scrollHeight > 0;
  },

  update,
});

onMounted(() => {
  onScroll(null, false);

  observer = new MutationObserver((mutations) => {
    check();
  });
  observer.observe(containerRef.value, {
    childList: true,
    subtree: true
  });
});

onBeforeUnmount(() => {
  if (!observer) return;
  observer.disconnect();
})

function onScroll(e, emitOnScroll = true) {
  if (emitOnScroll) emit('scroll');

  const el = e?.target || containerRef.value;
  clearTimeout(timer);

  if (!el.scrollTop && !el.scrollLeft) onScrollStart();
  if (!el.scrollLeft) onScrollStartX();

  check(el);
}

function check(el) {
  el = el || containerRef.value;
  const reachedEnd = el.scrollTop + el.clientHeight >= el.scrollHeight - OFFSET;
  const reachedEndX = el.scrollLeft + el.clientWidth >= el.scrollWidth - OFFSET;
  if (reachedEnd) onScrollEnd();
  if (reachedEndX) onScrollEndX();
}

function update() {
  // do something...
}

function onScrollStart() {
  emit('scrollStart');
}

function onScrollEnd() {
  emit('scrollEnd');
}

function onScrollStartX() {
  emit('scrollStartX');
}
function onScrollEndX() {
  emit('scrollEndX');
}
</script>
<style lang="scss">
@import "@/css/functions";
@import "@/css/variables";

$scrollbarSize: toRem(6);

::-webkit-scrollbar {
  width: $scrollbarSize;
  height: $scrollbarSize;
}

::-webkit-scrollbar-track {
  background: themeColor('neutral-100');
}

::-webkit-scrollbar-thumb {
  background: themeColor('neutral-400');
  border-radius: toRem(5);
}

::-webkit-scrollbar-thumb:hover {
  background: themeColor('neutral-600');
}

</style>
<style scoped lang="scss">
@import "@/css/functions";
@import "@/css/variables";

.scrollbar {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}

.loader {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: spacing(3) 0;
}
</style>